import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

interface TickerProps {
  steps: string[];
  slowAfterMs?: number;
  slowMessage?: string;
  className?: string;
}

/**
 * What a slow load is doing, one line at a time. A refresh is one request that
 * reads several providers in parallel, so the browser cannot see real
 * per-provider progress; `steps` are the work the request actually does, shown
 * in order, with a lighter line between each. Once `slowAfterMs` passes, the
 * line says what that wait almost always is instead of cycling on.
 */
export function LoadingTicker({ active, ...props }: TickerProps & { active: boolean }) {
  // Mounted only while active, so every load starts again from the first step.
  return active ? <Ticker {...props} /> : null;
}

function Ticker({ steps, slowAfterMs = 10_000, slowMessage, className }: TickerProps) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const started = Date.now();
    const timer = setInterval(() => setElapsed(Date.now() - started), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const tick = Math.floor(elapsed / TICK_MS);
  const line = elapsed >= slowAfterMs && slowMessage ? slowMessage : lineFor(steps, tick);
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs text-muted-foreground', className)} aria-live="polite">
      <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
      {line}
    </span>
  );
}

const TICK_MS = 1_600;

const FLAVOR = [
  'Pinging cell towers…',
  'Counting megabytes…',
  'Untangling Ethernet…',
  'Coaxing the LTE modem…',
  'Consulting the SIM oracle…',
  'Reticulating routes…',
  'Squinting at signal bars…',
  'Asking nicely…',
];

/** Real steps on even ticks, flavor between them; the last real step holds once reached. */
function lineFor(steps: string[], tick: number): string {
  if (tick % 2 === 1 && tick < steps.length * 2) return FLAVOR[Math.floor(tick / 2) % FLAVOR.length] ?? '';
  return steps[Math.min(Math.floor(tick / 2), steps.length - 1)] ?? '';
}
